import React from 'react';
import { Provider } from 'react-redux';
import loadable from '@loadable/component';
import { Theme } from '@commonsku/styles';
import '../../scss/css/app.css';
import configureStore from '../store/configureReskuStore';
import ErrorBoundary from '../components/ErrorBoundary';
import CskuApp from './CskuApp';
import ProjectRouter from '../components/ProjectRouter';
import { window } from '../global';
import createGlobalStyle, { additionalGlobalStyles } from '../components/helpers/createGlobalStyle';
import { datadogRum } from '@datadog/browser-rum';
import { Provider as DsProvider } from '@commonsku/design-system';
import AnalyticsWrapper from './AnalyticsWrapper';
import config from "../config";

if (config.datadog) {
  datadogRum.init(config.datadog);
}

const PUBLIC_SHOP_COMPONENTS = new Set([
  'PublicShopRouter',
  'PublicOrderShopRouter',
]);

const GlobalStyle = createGlobalStyle();

const NotFound = () => null;
const components = {
  AdminDecorationChargesApp: loadable(() => import('./AdminDecorationChargesApp')),
  AdminAvalaraApp: loadable(() => import('./AdminAvalaraApp')),
  AdminSettingsNewApp: loadable(() => import('./AdminSettingsNewApp')),
  AdminSettingsLoadingApp: loadable(() => import('./AdminSettingLoading')),
  AnalyticsApp: loadable(() => import('./AnalyticsApp')),
  SalesOrderApprove: loadable(() => import('./SalesOrderApprove')),
  AutoDKIM: loadable(() => import('./AutoDKIM')),
  ClientApp: loadable(() => import('./ClientApp')),
  CollectionRouter: loadable(() => import('../components/CollectionRouter')),
  CollectionsRouter: loadable(() => import('../components/CollectionsRouter')),
  CompanyRouter: loadable(() => import('../components/CompanyRouter')),
  CompanySearchApp: loadable(() => import('./CompanySearchApp')),
  ChatAgentApp: loadable(() => import('./ChatAgentApp')),
  CommunityEventsApp: loadable(() => import('../components/community-events')),
  CompanySetup: loadable(() => import('../components/company-setup/CompanySetup')),
  ContactRouter: loadable(() => import('../components/ContactRouter')),
  CskuApp,
  DashboardApp: loadable(() => import('./DashboardApp')),
  DistributorAddressesApp: loadable(() => import('./DistributorAddressesApp')),
  FileExplorer: loadable(() => import('./FileExplorer')),
  GalleryRouter: loadable(() => import('../components/GalleryRouter')),
  LicenseChangeApp: loadable(() => import('./LicenseChangeApp')),
  MarketingSettingsApp: loadable(() => import('./MarketingSettingsApp')),
  XeroAdmin: loadable(() => import('../components/XeroAdmin')),
  PresentApp: loadable(() => import('./PresentApp')),
  ProductsApp: loadable(() => import('./ProductsApp')),
  SalesAnalyticsApp: loadable(() => import('./SalesAnalyticsApp')),
  FinanceAnalyticsApp: loadable(() => import('./FinanceAnalyticsApp')),
  FinancePaymentsApp: loadable(() => import('./FinancePaymentsApp')),
  ManagementAnalyticsApp: loadable(() => import('./ManagementAnalyticsApp')),
  ManageThemesApp: loadable(() => import('./ManageThemesApp')),
  ProjectDashboardApp: loadable(() => import('./ProjectDashboardApp')),
  ProjectRouter,
  ProofApproval: loadable(() => import('./ProofApproval')),
  PSChargesReportApp: loadable(() => import('./PSChargesReportApp')),
  PSReportApp: loadable(() => import('./PSReportApp')),
  PSSupplierApp: loadable(() => import('./PSSupplierApp')),
  PublicShopRouter: loadable(() => import('../components/shop/PublicShopRouter')),
  PublicOrderShopRouter: loadable(() => import('../components/PublicOrderShopRouter')),
  PortalRouter: loadable(() => import('../components/PortalRouter')),
  PromoDataProductApp: loadable(() => import('./PromoDataProductApp')),
  ShopsFeesApp: loadable(() => import('./ShopsFeesApp')),
  ShopRouter: loadable(() => import('../components/shop/ShopRouter')),
  ShopsRouter: loadable(() => import('../components/shop/ShopsRouter')),
  SuppliersApp: loadable(() => import('./SuppliersApp')),
  TeamAutoDKIM: loadable(() => import('../components/CompanyEmailDomainTable')),
  SelectFileApp: loadable(() => import('./SelectFileApp')),
  ProductionReportApp: loadable(() => import('./ProductionReportApp')),
  MarketingSoftwareApp: loadable(() => import('./MarketingSoftwareApp')),
  ClientPortalRouter: loadable(() => import('../components/client-portal/ClientPortalRouter')),
  LoginApp: loadable(() => import('./LoginApp')),
  EmptyApp: loadable(() => import('./EmptyApp')),
  ResetPasswordApp: loadable(() => import('./ResetPasswordApp')),
  TwoFactorSetupApp: loadable(() => import('./TwoFactorSetupApp')),
  CompanyActivationApp: loadable(() => import('./CompanyActivationApp')),
  UserInvitationApp: loadable(() => import('./UserInvitationApp')),
  AdminUsersNewApp: loadable(() => import('./AdminUsersNewApp')),
  ReportApp: loadable(() => import('./ReportApp')),
  SupplierBillingApp: loadable(() => import('./SupplierBillingApp')),
  AllowDeny: loadable(()=>import('../components/AllowDeny')),
  ManageDistributorApp: loadable(() => import('./ManageDistributorApp')),
  StartDiscussion: loadable(() => import('../components/StartDiscussion')),
  FinanceApp: loadable(() => import('./FinanceApp')),
  PodProductionReportApp: loadable(() => import('./PodProductionReportApp')),
  WorkflowAutomationsApp: loadable(() => import('./WorkflowAutomationsApp')),
  ShopTemplatesApp: loadable(() => import('./ShopTemplatesApp')),
  ExternalShopifyApp: loadable(() => import('../shopify/components/ShopifyApp')),
  UnsubscribeApp: loadable(() => import('./UnsubscribeApp')),
  TechSupportApp: loadable(() => import('./TechSupportApp')),
  SkubotChatsApp: loadable(() => import('./SkubotChatsApp')),
  PaymentStatusApp: loadable(() => import('./PaymentStatusApp')),

  ///////////////// example ///////////////////////
  ReskuExampleApp: loadable(() => import('./ReskuExampleApp')),
};

const App = ({ component, initialState, defaultReducers, ...props }) => {
  const LazyComponent = components[component] || NotFound;
  if (typeof (window.initialState) === 'undefined') {
    window.initialState = initialState;
  }

  const isPublicShop = PUBLIC_SHOP_COMPONENTS.has(component);

  const store = configureStore(initialState || window.initialState, defaultReducers);

  return <Provider store={store}>
    <ErrorBoundary>
      <DsProvider>
        <Theme globalStyles>
          <GlobalStyle skipLegacyFonts={isPublicShop} additionalGlobalStyles={additionalGlobalStyles} />
          <AnalyticsWrapper>
            <LazyComponent attachReducers={store.attachReducers} {...props}/>
          </AnalyticsWrapper>
        </Theme>
      </DsProvider>
    </ErrorBoundary>
  </Provider>;
};

export default App;
